import { useEffect, useState, type ReactNode } from "react";

import { Alert, Card, CardTitle, NativeSelect } from "@realroof/ui";

import { api } from "../api";
import { Table, td, th } from "../table";
import { CityChooser, type Place } from "./Locations";

type Row = Record<string, string | number | null>;
interface Data {
  overview: Record<string, number | null>;
  new_ads_by_module: Record<string, number>;
  conversion: Record<string, number | null>;
  daily: { day: string; submitted: number; approved: number; rejected: number; revenue_paise: number }[];
  revenue_by_pack: { month: string; pack: string; orders: number; paise: number }[];
  rejection_reasons: { reason: string; count: number }[];
  top_cities: Row[];
  top_localities: Row[];
  zero_results: { search: string; count: number }[];
  boxes: Row[];
  team: Row[];
}

const n = (v: number | null | undefined) => (v == null ? "–" : v.toLocaleString("en-IN"));
const money = (paise: number | null | undefined) => (paise == null ? "–" : `₹${Math.round(paise / 100).toLocaleString("en-IN")}`);
const pct = (v: number | null | undefined) => (v == null ? "–" : `${v}%`);

function Tile({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <Card className="gap-0.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-display text-2xl font-bold">{value}</span>
      {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </Card>
  );
}

/** Simple bars, no chart library: one bar per day (or month), scaled to the largest. */
function Bars({ label, items }: { label: string; items: { key: string; value: number; title: string }[] }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <figure className="m-0 flex flex-col gap-1">
      <figcaption className="text-sm font-semibold">{label}</figcaption>
      <div className="flex h-28 items-end gap-0.5 border-b border-border" role="img" aria-label={label}>
        {items.map((i) => (
          <div key={i.key} title={i.title} className="min-w-1 flex-1 rounded-t bg-primary/80" style={{ height: `${(100 * i.value) / max}%` }} />
        ))}
      </div>
    </figure>
  );
}

function Simple({ title, head, rows, empty }: { title: string; head: string[]; rows: ReactNode[][]; empty: string }) {
  return (
    <Card>
      <CardTitle className="text-base">{title}</CardTitle>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <Table>
          <thead>
            <tr>
              {head.map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j} className={td}>
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/** Analytics (wireframe A12): supply, demand, money, search gaps, box ads and the team, for a period and city. */
export function Analytics() {
  const [days, setDays] = useState(30);
  const [city, setCity] = useState<Place | null>(null);
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    api<Data>(`/admin/analytics?days=${days}${city ? `&city=${city.id}` : ""}`)
      .then(setData)
      .catch((err: Error) => setError(err.message));
  }, [days, city]);

  const o = data?.overview ?? {};
  const c = data?.conversion ?? {};
  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Analytics</h1>
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 text-sm">
          Period
          <NativeSelect value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </NativeSelect>
        </label>
        <CityChooser label="City (all if empty)" value={city} onChange={setCity} />
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      {!data && !error && <p className="text-muted-foreground">Loading…</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Tile label="Revenue (before GST, after refunds)" value={money(o.revenue_paise)} sub={`${n(o.orders)} orders · ${n(o.paying_users)} payers · ARPU ${money(o.arpu_paise)}`} />
            <Tile label="Live ads" value={n(o.live_ads)} sub={`${n(o.premium_live)} premium`} />
            <Tile label="Leads" value={n(o.leads)} sub={`${o.leads_per_live_ad ?? "–"} per live ad · premium ${o.leads_per_premium_ad ?? "–"} vs free ${o.leads_per_free_ad ?? "–"}`} />
            <Tile label="New users" value={n(o.new_users)} sub={`${n(o.verified_mobiles)} mobiles verified`} />
            <Tile label="Ads submitted" value={n(o.ads_submitted)} sub={`property ${n(data.new_ads_by_module.property)} · halls ${n(data.new_ads_by_module.hall)} · services ${n(data.new_ads_by_module.service)}`} />
            <Tile label="Approval rate" value={pct(o.approval_rate)} sub={`${n(o.ads_approved)} approved · ${n(o.ads_rejected)} rejected · median ${o.median_review_hours ?? "–"} h`} />
            <Tile label="Free → premium" value={pct(c.free_to_premium_pct)} sub={`${n(c.premium_buyers)} of ${n(c.posters)} posters · repeat ${pct(c.repeat_buyers_pct)}`} />
            <Tile label="Credits" value={`${n(o.credits_sold)} sold`} sub={`${n(c.credits_used)} used all time · ${n(c.credits_unused)} unused`} />
          </div>

          <Card className="grid gap-4 md:grid-cols-2">
            <Bars
              label="Ads submitted per day"
              items={data.daily.map((d) => ({ key: d.day, value: d.submitted, title: `${d.day}: ${d.submitted} submitted, ${d.approved} approved, ${d.rejected} rejected` }))}
            />
            <Bars label="Revenue per day" items={data.daily.map((d) => ({ key: d.day, value: d.revenue_paise, title: `${d.day}: ${money(d.revenue_paise)}` }))} />
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Simple
              title="Revenue by pack and month (12 months)"
              head={["Month", "Pack", "Orders", "Revenue"]}
              rows={data.revenue_by_pack.map((r) => [r.month, r.pack, n(r.orders), money(r.paise)])}
              empty="No payments yet."
            />
            <Simple title="Rejection reasons" head={["Reason", "Ads"]} rows={data.rejection_reasons.map((r) => [r.reason, n(r.count)])} empty="No rejections in this period." />
            <Simple
              title="Top cities"
              head={["City", "Live", "Premium", "Searches", "Leads", "Boxes live"]}
              rows={data.top_cities.map((r) => [r.city, n(r.live as number), n(r.premium as number), n(r.searches as number), n(r.leads as number), `${r.boxes_live} / 8`])}
              empty="No ads or searches yet."
            />
            <Simple
              title="Top searched localities"
              head={["Locality", "Searches", "Live ads", "No results"]}
              rows={data.top_localities.map((r) => [`${r.locality ?? r.locality_id}${r.city ? `, ${r.city}` : ""}`, n(r.searches as number), n(r.live as number), pct(r.zero_pct as number)])}
              empty="No locality searches in this period."
            />
            <Simple
              title="Searches that found nothing (supply gaps)"
              head={["Search", "Times"]}
              rows={data.zero_results.map((z) => [z.search, n(z.count)])}
              empty="Every search found something."
            />
            <Simple
              title="Home box ads"
              head={["City", "Boxes", "Shown", "Clicks", "CTR", "Leads"]}
              rows={data.boxes.map((b) => [b.city, n(b.boxes as number), n(b.impressions as number), n(b.clicks as number), pct(b.ctr_pct as number), n(b.leads as number)])}
              empty="No box ads in this period."
            />
          </div>
          <Simple
            title="Team"
            head={["Staff", "Ads reviewed", "Approved", "Rejected", "Taken down", "Other checks", "Median time to review"]}
            rows={data.team.map((t) => [t.staff, n(t.reviewed as number), n(t.approved as number), n(t.rejected as number), n(t.taken_down as number), n(t.other_checks as number), t.median_minutes == null ? "–" : `${t.median_minutes} min`])}
            empty="No reviews in this period."
          />
        </>
      )}
    </section>
  );
}
